import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { auth as authApi } from "../api/resources";
import { ErrorBanner, Field, Loading } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import type { InvitationPreview } from "../lib/types";

/**
 * The page an emailed invitation link opens.
 *
 * It shows who invited you and what you are being asked to do *before* asking for anything, then
 * accepts and signs in as one step — an invitee who has to find the login page separately is an
 * invitee who does not arrive.
 */
export function AcceptInvitePage() {
  const { token = "" } = useParams();
  const { adopt } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<InvitationPreview | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authApi
      .previewInvitation(token)
      .then((body) => {
        if (cancelled) return;
        setPreview(body);
        setFullName(body.full_name ?? "");
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function accept(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const session = await authApi.acceptInvitation(token, {
        password: password || undefined,
        full_name: fullName || undefined,
      });
      adopt(session.access_token, session.user, session.memberships);
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-card__brand">
            <strong>This invitation cannot be used</strong>
          </div>
          <p className="muted">{loadError}</p>
          <p className="small muted" style={{ marginBottom: 0 }}>
            Links expire, and a new invitation replaces the previous one. Ask whoever invited you
            to send another.
          </p>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Loading label="Checking your invitation…" />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={(event) => void accept(event)}>
        <div className="auth-card__brand">
          <strong>{preview.organization_name}</strong>
          <span>
            {preview.invited_by ? `${preview.invited_by} invited you` : "You have been invited"} as{" "}
            {preview.role_label.toLowerCase()}
          </span>
        </div>

        <ErrorBanner error={error} />

        {preview.message ? (
          <div className="banner banner--ok" style={{ fontStyle: "italic" }}>
            “{preview.message}”
          </div>
        ) : null}

        <p className="small muted">
          {preview.interview_title
            ? `You have been asked to complete: ${preview.interview_title}.`
            : "You will be asked how your part of the business actually works — which systems you use, what you check, what goes wrong."}{" "}
          It saves as you go, and “I don’t know” is a useful answer: we record it as something
          still to find out rather than guessing.
        </p>

        {preview.role_description ? (
          <p className="tiny faint">{preview.role_description}</p>
        ) : null}

        <Field label="Email">
          <input value={preview.email} readOnly disabled />
        </Field>

        <Field label="Your name">
          <input
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            placeholder="How you would like to be credited"
          />
        </Field>

        {preview.needs_password ? (
          <Field label="Choose a password" hint="At least 8 characters.">
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </Field>
        ) : (
          <p className="small muted">
            You already have an account here, so this just adds {preview.organization_name} to it.
          </p>
        )}

        <button className="primary" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "Setting up…" : "Accept and get started"}
        </button>
      </form>
    </div>
  );
}
